import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  resolveUrl,
  localizedStoreProductName,
  localizedStoreProductDescription,
  isStoreArabicLang,
  isStoreProductOutOfStock,
  formatStoreDeliveryEstimate,
  computeCurtainUnitPrice,
  getStoreProductPriceDisplay,
} from "../../utils/storeUtils";
import { StoreProductPriceBlock } from "./components/StoreProductPriceBlock";
import Seo from "../../components/Seo/Seo";
import { ProductJsonLd } from "../../components/Seo/JsonLd";
import { useTranslation } from "react-i18next";
import { pixelViewContent, pixelAddToCart } from "../../utils/fbPixel";
import { useStoreCart } from "./StoreCartContext";
import { Lens } from "../../components/ui/Lens";
import { indexCategoryTree } from "../../utils/categoryTreeUtils";
import OptionButton from "./components/OptionButton";
import SimpleMarkdown from "./components/SimpleMarkdown";
import "./ProductDetail.css";
import {
  convertDisplayRows,
  hasValidSizeChart,
  normalizeSizeChartUnit,
} from "../../utils/sizeChartUtils";
import { trackEvent } from "../../utils/trackEvent";

function safeId(v) {
  if (!v) return "";
  if (typeof v === "object" && v._id) return String(v._id);
  return String(v);
}

function getRootSlugForCategoryId(categoryId, treeIndex) {
  const id = String(categoryId || "").trim();
  if (!id || !treeIndex?.byId || !treeIndex?.parentById) return "";
  let cur = treeIndex.byId.get(id) || null;
  let guard = 0;
  while (cur && cur?.parentId && guard < 40) {
    const pid = safeId(cur.parentId);
    const next = pid ? treeIndex.byId.get(pid) : null;
    if (!next) break;
    cur = next;
    guard += 1;
  }
  return String(cur?.slug || "").trim().toLowerCase();
}

function getCategorySlugForCategoryId(categoryId, treeIndex) {
  const id = String(categoryId || "").trim();
  if (!id || !treeIndex?.byId) return "";
  return String(treeIndex.byId.get(id)?.slug || "").trim().toLowerCase();
}

const ROLL_CURTAIN_SLUGS = new Set([
  "rollerblinds",
  "rollersunscreen",
  "rollerblackout",
]);

function isRollCurtainProduct(product, treeIndex) {
  if (getRootSlugForCategoryId(product?.categoryId, treeIndex) !== "curtains") return false;
  const slug = getCategorySlugForCategoryId(product?.categoryId, treeIndex);
  if (ROLL_CURTAIN_SLUGS.has(slug)) return true;
  return slug.includes("roller");
}

export default function ProductDetail() {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState(null);
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);
  const [categoryTree, setCategoryTree] = useState([]);
  const { addToCart: addToCartContext, toggleWishlist, isInWishlist } = useStoreCart();
  const [descExpanded, setDescExpanded] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [customerReviews, setCustomerReviews] = useState([]);
  const [reviewsAvg, setReviewsAvg] = useState(0);
  const relatedScrollRef = useRef(null);
  const relPausedRef = useRef(false);

  // Scroll helpers — always read from DOM so refs never go stale
  const relScroll = useCallback((dir) => {
    const el = relatedScrollRef.current;
    if (!el) return;
    const card = el.querySelector(".store-related-card");
    const step = (card?.offsetWidth || 230) + 16;
    if (dir === "next") {
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (el.scrollLeft >= maxScroll - 4) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: step, behavior: "smooth" });
      }
    } else {
      el.scrollBy({ left: -step, behavior: "smooth" });
    }
  }, []);

  const relNextFn = useCallback(() => relScroll("next"), [relScroll]);
  const relPrevFn = useCallback(() => relScroll("prev"), [relScroll]);

  const apiBaseUrl = axios.defaults.baseURL || "";
  const resolveProductUrl = useCallback((url) => resolveUrl(url, apiBaseUrl), [apiBaseUrl]);
  const findColorImageIndex = useCallback(
    (colorEntry, galleryImages) => {
      if (!colorEntry || !Array.isArray(galleryImages) || galleryImages.length === 0) return 0;
      const indexFromColor = Number(colorEntry.imageIndex);
      if (
        Number.isInteger(indexFromColor) &&
        indexFromColor >= 0 &&
        indexFromColor < galleryImages.length
      ) {
        return indexFromColor;
      }
      if (colorEntry.imageUrl) {
        const matchedIndex = galleryImages.findIndex((img) => img?.url === colorEntry.imageUrl);
        if (matchedIndex >= 0) return matchedIndex;
      }
      return 0;
    },
    []
  );

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`/store/products/${productId}`)
      .then(({ data }) => {
        if (!cancelled) {
          setProduct(data);
          const productImages =
            data?.images?.length > 0 ? data.images : data?.image?.url ? [data.image] : [];
          trackEvent("product_view", {
            productId: String(data._id || ""),
            productName: data.name || "",
            productArName: data.arName || "",
            productImageUrl: productImages[0]?.url || "",
          });
          pixelViewContent(data);
          setSelectedColor(data?.colors?.[0]?.name || null);
          if (data?.colors?.length > 0) {
            const firstColorIndex = findColorImageIndex(data.colors[0], productImages);
            setGalleryIndex(firstColorIndex);
          } else {
            setGalleryIndex(0);
          }
        }
      })
      .catch(() => {
        if (!cancelled)
          toast.error(t("store.productNotFound", { defaultValue: "Product not found." }));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [productId, findColorImageIndex, t]);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    axios
      .get(`/store/products/${productId}/related`)
      .then(({ data }) => { if (!cancelled) setRelatedProducts(Array.isArray(data) ? data : []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productId]);

  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    axios
      .get(`/store/product-reviews?productId=${productId}`)
      .then(({ data }) => {
        if (cancelled) return;
        setCustomerReviews(Array.isArray(data.reviews) ? data.reviews : []);
        setReviewsAvg(data.avg || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [productId]);

  // Auto-play: empty deps so interval is never cleared on re-renders.
  // relScroll reads from DOM at call time — no stale closure issue.
  useEffect(() => {
    const id = setInterval(() => {
      if (!relPausedRef.current) relScroll("next");
    }, 3500);
    return () => clearInterval(id);
  }, [relScroll]); // relScroll is stable (useCallback [])

  useEffect(() => {
    let cancelled = false;
    axios
      .get("/store/categories/tree")
      .then(({ data }) => {
        if (cancelled) return;
        setCategoryTree(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setCategoryTree([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const images = product?.images?.length ? product.images : (product?.image?.url ? [product.image] : []);
  const colors = product?.colors || [];
  const effectiveColorName = selectedColor ?? colors[0]?.name ?? null;
  const effectiveColor = colors.find((c) => c.name === effectiveColorName) || null;
  const getSwatchBackground = useCallback((colorEntry) => {
    if (!colorEntry) return "#ccc";
    const hexesRaw = Array.isArray(colorEntry.hexes) ? colorEntry.hexes : [];
    const hexes = hexesRaw.map((h) => String(h || "").trim()).filter(Boolean);
    const fallback = String(colorEntry.hex || "").trim();
    const list = hexes.length > 0 ? hexes : fallback ? [fallback] : [];
    if (list.length <= 1) return list[0] || "#ccc";
    const step = 100 / list.length;
    const stops = list
      .map((hex, idx) => `${hex} ${Math.floor(idx * step)}% ${Math.floor((idx + 1) * step)}%`)
      .join(", ");
    return `conic-gradient(from 90deg, ${stops})`;
  }, []);

  const treeIndex = useMemo(() => indexCategoryTree(categoryTree), [categoryTree]);
  const isCurtainsCategory = useMemo(() => {
    const rootSlug = getRootSlugForCategoryId(product?.categoryId, treeIndex);
    return rootSlug === "curtains";
  }, [product?.categoryId, treeIndex]);

  const isRollCurtain = useMemo(
    () => isRollCurtainProduct(product, treeIndex),
    [product, treeIndex]
  );

  const cmUnitLabel = useMemo(() => (isRtl ? "سم" : "cm"), [isRtl]);
  const labels = useMemo(
    () => ({
      width: isRtl ? "العرض" : "Width",
      height: isRtl ? "الارتفاع" : "Height",
      widthAdj: isRtl ? "تعديل العرض" : "Width adjustment",
      heightAdj: isRtl ? "تعديل الارتفاع" : "Height adjustment",
      chain: isRtl ? "نوع السلسلة" : "Chain type",
      metal: isRtl ? "معدن" : "Metal",
      plastic: isRtl ? "بلاستيك" : "Plastic",
    }),
    [isRtl]
  );

  const WIDTH_SIZES = useMemo(() => [100, 125, 150, 175, 200, 225], []);
  const HEIGHT_SIZES = useMemo(() => [150, 175, 200, 225, 250, 275], []);
  const ADJUSTMENTS = useMemo(
    () => [
      { id: "none", labelEn: "No adjustment", labelAr: "بدون تعديل" },
      { id: "+5", labelEn: "Add +5 cm", labelAr: "إضافة +٥ سم" },
      { id: "+10", labelEn: "Add +10 cm", labelAr: "إضافة +١٠ سم" },
      { id: "-5", labelEn: "Remove -5 cm", labelAr: "خصم -٥ سم" },
      { id: "-10", labelEn: "Remove -10 cm", labelAr: "خصم -١٠ سم" },
    ],
    []
  );

  const [selectedWidth, setSelectedWidth] = useState(null);
  const [selectedHeight, setSelectedHeight] = useState(null);
  const [widthAdjustment, setWidthAdjustment] = useState("none");
  const [heightAdjustment, setHeightAdjustment] = useState("none");
  const [chainType, setChainType] = useState("metal");

  useEffect(() => {
    if (!isCurtainsCategory) return;
    // Initialize sensible defaults once product is loaded.
    setSelectedWidth((prev) => (prev == null ? WIDTH_SIZES[0] : prev));
    setSelectedHeight((prev) => (prev == null ? HEIGHT_SIZES[0] : prev));
    setWidthAdjustment((prev) => (prev ? prev : "none"));
    setHeightAdjustment((prev) => (prev ? prev : "none"));
    if (isRollCurtain) {
      setChainType((prev) => (prev ? prev : "metal"));
    }
  }, [isCurtainsCategory, isRollCurtain, WIDTH_SIZES, HEIGHT_SIZES]);

  const handleAddToCart = () => {
    if (!product) return;
    if (isStoreProductOutOfStock(product)) {
      toast.error(t("store.outOfStock", { defaultValue: "Out of stock" }));
      return;
    }
    const curtainPrice = isCurtainsCategory
      ? computeCurtainUnitPrice(product, {
          selectedWidth,
          selectedHeight,
          widthAdjustment,
          heightAdjustment,
          ...(isRollCurtain ? { chainType } : {}),
        })
      : null;
    addToCartContext(product, {
      selectedColor: colors.length > 0 ? effectiveColorName : undefined,
      selectedColorHex: effectiveColor?.hexes?.[0] || effectiveColor?.hex || undefined,
      ...(isCurtainsCategory
        ? {
            selectedWidth,
            selectedHeight,
            widthAdjustment,
            heightAdjustment,
            ...(isRollCurtain ? { chainType } : {}),
          }
        : {}),
      ...(curtainPrice
        ? {
            priceOverride: curtainPrice.finalPrice,
            originalPriceOverride: curtainPrice.originalPrice,
            discountPercentOverride: curtainPrice.discountPercent,
          }
        : {}),
    });
    pixelAddToCart(product, 1, undefined, curtainPrice?.finalPrice);
    const label = localizedStoreProductName(product, i18n.language);
    toast.success(
      label
        ? t("store.toastProductAddedToCart", {
            name: label,
            defaultValue: "{{name}} added to cart.",
          })
        : t("store.toastProductAddedToCartGeneric", {
            defaultValue: "Product added to cart.",
          })
    );
  };

  const handleShare = useCallback(async () => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    const title = localizedStoreProductName(product, i18n.language) || "";
    const text = title;
    try {
      if (navigator.share) {
        await navigator.share({ title, text, url });
        return;
      }
    } catch {
      // If user cancels share sheet, silently fall back to copy.
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("store.shareCopied", { defaultValue: "Link copied." }));
    } catch {
      toast.error(t("store.shareFailed", { defaultValue: "Couldn't share. Please copy the link." }));
    }
  }, [i18n.language, product, t]);

  useEffect(() => {
    if (!isSizeChartOpen) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setIsSizeChartOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isSizeChartOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!isSizeChartOpen) return undefined;

    const onAfterPrint = () => {
      document.documentElement.classList.remove("store-print-sizechart");
      document.body.classList.remove("store-print-sizechart");
    };
    window.addEventListener("afterprint", onAfterPrint);
    return () => window.removeEventListener("afterprint", onAfterPrint);
  }, [isSizeChartOpen]);

  const rawSizeChart = product?.sizeChart || null;
  const hasSizeChart = hasValidSizeChart(rawSizeChart);

  const normalizedChartUnit = hasSizeChart ? normalizeSizeChartUnit(rawSizeChart?.unit) : "cm";
  // Customer-facing: show only the saved (admin-selected) unit.
  const effectiveSizeChartUnit = normalizedChartUnit;

  const sizeChartColumns = hasSizeChart
    ? rawSizeChart.columns
        .map((c) => {
          if (typeof c === "string") return c;
          if (c && typeof c === "object") return isRtl ? c.arLabel || c.label : c.label || c.arLabel;
          return "";
        })
        .map((s) => String(s || "").trim())
        .filter(Boolean)
    : [];

  const sizeChartRows = useMemo(() => {
    if (!hasSizeChart) return [];
    const rows = Array.isArray(rawSizeChart?.rows) ? rawSizeChart.rows : [];
    return rows
      .filter((r) => Array.isArray(r))
      .map((r) => r.map((cell) => String(cell ?? "").trim()));
  }, [hasSizeChart, rawSizeChart]);

  const displayedSizeChartRows = useMemo(() => {
    if (!hasSizeChart) return [];
    return convertDisplayRows(sizeChartRows, normalizedChartUnit, normalizedChartUnit);
  }, [hasSizeChart, normalizedChartUnit, sizeChartRows]);

  if (loading) {
    return (
      <div className="store-product-detail store-product-detail-loading">
        <div className="store-loading-spinner" aria-hidden="true" />
        <p>{t("store.loading", { defaultValue: "Loading…" })}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="store-product-detail store-product-detail-error">
        <p>{t("store.productNotFound", { defaultValue: "Product not found." })}</p>
        <Link to="/store">{t("store.backToStore", { defaultValue: "Back to store" })}</Link>
      </div>
    );
  }

  const displayName = localizedStoreProductName(product, i18n.language);
  const longDescription = localizedStoreProductDescription(product, i18n.language);
  const descriptionText = String(longDescription || "").trim();
  const descriptionIsLong = descriptionText.length > 520 || descriptionText.split("\n").length > 10;
  const outOfStock = isStoreProductOutOfStock(product);
  const deliveryEstimate = formatStoreDeliveryEstimate(product, t);
  const mainGalleryImg = images[galleryIndex];
  const mainImgAlt =
    (isStoreArabicLang(i18n.language)
      ? mainGalleryImg?.arAlt || mainGalleryImg?.alt
      : mainGalleryImg?.alt || mainGalleryImg?.arAlt) ||
    displayName ||
    t("store.checkout.productAlt", { defaultValue: "Product" });

  const sizeChartTitle = hasSizeChart
    ? (isRtl ? rawSizeChart?.arTitle : rawSizeChart?.title) ||
      t("store.sizeChart.open", { defaultValue: "Size chart" })
    : "";
  const sizeChartNote = hasSizeChart ? (isRtl ? rawSizeChart?.arNote : rawSizeChart?.note) || "" : "";

  const sizeChartSubtitle = hasSizeChart
    ? t("store.sizeChart.unitHint", {
        unit: effectiveSizeChartUnit,
        defaultValue: "All measurements in {{unit}}.",
      })
    : "";

  const metaDescription = descriptionText
    ? descriptionText.slice(0, 160)
    : `${displayName} — ${product.arCategory || product.category || ""} من الرملاء للصناعات الخشبية.`.trim();
  const curtainBasePrice = product.curtainPricing?.enabled
    ? Number(product.curtainPricing.basePrice)
    : NaN;
  const discountPct = Math.max(0, Math.min(100, Math.floor(Number(product.discount_percentage) || 0)));
  const jsonLdPrice = Number.isFinite(curtainBasePrice)
    ? Math.round((curtainBasePrice - (curtainBasePrice * discountPct) / 100) * 100) / 100
    : getStoreProductPriceDisplay(product).finalPrice;
  const seoImage = mainGalleryImg?.url ? resolveProductUrl(mainGalleryImg.url) : undefined;

  return (
    <>
      <Seo
        title={displayName}
        description={metaDescription}
        path={`/store/product/${product._id}`}
        image={seoImage}
        type="product"
        price={jsonLdPrice}
        availability={outOfStock ? "OutOfStock" : "InStock"}
      />
      <ProductJsonLd
        product={product}
        displayName={displayName}
        displayDescription={metaDescription}
        finalPrice={jsonLdPrice}
        path={`/store/product/${product._id}`}
      />
    <div className="store-product-detail">
      <Link to="/store" className="store-product-detail-back">
        <i className={`uil ${isRtl ? "uil-arrow-right" : "uil-arrow-left"}`} />{" "}
        {t("store.backToStore", { defaultValue: "Back to store" })}
      </Link>

      <div className="store-product-detail-grid">
        <div className="store-product-detail-gallery-col">
          <div className="store-product-detail-gallery-sticky">
            <div className="store-product-detail-gallery">
              <div className="store-product-detail-main">
                {images[galleryIndex] && (
                  <Lens
                    zoomFactor={1.45}
                    lensSize={200}
                    ariaLabel={t("store.productImageZoom", {
                      defaultValue: "Product image — hover to zoom",
                    })}
                  >
                    <img
                      src={resolveProductUrl(images[galleryIndex].url)}
                      alt={mainImgAlt}
                      style={{ objectFit: "cover" }}
                    />
                  </Lens>
                )}
              </div>
              {images.length > 1 && (
                <>
                  <div className="store-product-detail-thumbs">
                    {images.map((img, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`store-product-detail-thumb${galleryIndex === i ? " active" : ""}`}
                        onClick={() => setGalleryIndex(i)}
                      >
                        <img
                          src={resolveProductUrl(img.url)}
                          alt={
                            (isStoreArabicLang(i18n.language)
                              ? img?.arAlt || img?.alt
                              : img?.alt || img?.arAlt) ||
                            displayName ||
                            t("store.checkout.productAlt", { defaultValue: "Product" })
                          }
                          style={{ objectFit: "cover" }}
                        />
                      </button>
                    ))}
                  </div>
                  <div className="store-product-detail-arrows">
                    <button
                      type="button"
                      onClick={() =>
                        setGalleryIndex((i) =>
                          isRtl
                            ? i >= images.length - 1
                              ? 0
                              : i + 1
                            : i <= 0
                            ? images.length - 1
                            : i - 1
                        )
                      }
                      aria-label={t("store.productImagePrev", { defaultValue: "Previous image" })}
                    >
                      <i className={`uil ${isRtl ? "uil-angle-right-b" : "uil-angle-left-b"}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() =>
                        setGalleryIndex((i) =>
                          isRtl
                            ? i <= 0
                              ? images.length - 1
                              : i - 1
                            : i >= images.length - 1
                            ? 0
                            : i + 1
                        )
                      }
                      aria-label={t("store.productImageNext", { defaultValue: "Next image" })}
                    >
                      <i className={`uil ${isRtl ? "uil-angle-left-b" : "uil-angle-right-b"}`} />
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="store-product-detail-info">
          <h1 className="store-product-detail-title">{displayName}</h1>
          {product?.sku ? (
            <div className="store-product-detail-serial">
              <span className="store-product-detail-serial-label">
                {t("store.productSerial", { defaultValue: "Product code" })}
              </span>
              <button
                type="button"
                className="store-product-detail-serial-code"
                onClick={() => {
                  navigator.clipboard
                    .writeText(String(product.sku))
                    .then(() =>
                      toast.success(
                        t("store.productSerialCopied", { defaultValue: "Product code copied." })
                      )
                    )
                    .catch(() =>
                      toast.error(
                        t("store.productSerialCopyFailed", { defaultValue: "Could not copy." })
                      )
                    );
                }}
                title={t("store.productSerialCopyHint", { defaultValue: "Click to copy" })}
              >
                {product.sku}
              </button>
            </div>
          ) : null}
          {descriptionText ? (
            <div
              className={`store-product-detail-description${
                !descExpanded && descriptionIsLong ? " is-clamped" : ""
              }`}
            >
              <SimpleMarkdown text={descriptionText} />
            </div>
          ) : null}
          {descriptionText && descriptionIsLong ? (
            <button
              type="button"
              className="store-product-detail-desc-toggle"
              onClick={() => setDescExpanded((v) => !v)}
            >
              {descExpanded ? (isRtl ? "عرض أقل" : "See less") : (isRtl ? "عرض المزيد…" : "See more…")}
            </button>
          ) : null}
          {!outOfStock ? (
          <p className="store-product-detail-price">
            {(() => {
              const curtainPrice = isCurtainsCategory
                ? computeCurtainUnitPrice(product, {
                    selectedWidth,
                    selectedHeight,
                    widthAdjustment,
                    heightAdjustment,
                    ...(isRollCurtain ? { chainType } : {}),
                  })
                : null;
              if (!curtainPrice) return <StoreProductPriceBlock product={product} size="large" />;
              const patched = {
                ...product,
                original_price: curtainPrice.originalPrice,
                final_price: curtainPrice.finalPrice,
                discount_percentage: curtainPrice.discountPercent,
              };
              return <StoreProductPriceBlock product={patched} size="large" />;
            })()}
          </p>
          ) : null}
          {deliveryEstimate ? (
            <p className="store-product-detail-delivery">{deliveryEstimate}</p>
          ) : null}
          <p className="store-product-detail-stock">
            {!outOfStock ? (
              <span className="in-stock">
                <i className="uil uil-check-circle" />{" "}
                {t("store.inStock", { defaultValue: "In stock" })}
              </span>
            ) : (
              <span className="out-of-stock">
                <i className="uil uil-times-circle" />{" "}
                {t("store.outOfStock", { defaultValue: "Out of stock" })}
              </span>
            )}
          </p>
          {colors.length > 0 && (
            <div className="store-product-detail-colors">
              <span className="store-product-detail-colors-label">
                {t("store.productColorLabel", { defaultValue: "Color:" })}
              </span>
              {colors.map((c) => (
                <button
                  key={c.name}
                  type="button"
                  className={`store-product-detail-color-swatch${effectiveColorName === c.name ? " selected" : ""}`}
                  data-color-name={c.name}
                  style={{ background: getSwatchBackground(c) }}
                  onClick={() => {
                    setSelectedColor(c.name);
                    setGalleryIndex(findColorImageIndex(c, images));
                  }}
                  aria-label={t("store.quickViewSelectColor", {
                    name: c.name,
                    defaultValue: "Select {{name}}",
                  })}
                >
                </button>
              ))}
              {effectiveColorName && (
                <span className="store-product-detail-colors-label">
                  {t("store.selected", { defaultValue: "Selected:" })} {effectiveColorName}
                </span>
              )}
            </div>
          )}

          {isCurtainsCategory ? (
            <div className="store-product-options">
              <div className="store-product-options-section">
                <div className="store-product-options-head">
                  <span className="store-product-options-title">{labels.width}</span>
                  {selectedWidth != null ? (
                    <span className="store-product-options-value">
                      {selectedWidth} {cmUnitLabel}
                    </span>
                  ) : null}
                </div>
                <div className="store-product-options-grid" role="list">
                  {WIDTH_SIZES.map((n) => {
                    const disabled = false;
                    return (
                      <OptionButton
                        key={`w-${n}`}
                        label={`${n} ${cmUnitLabel}`}
                        selected={Number(selectedWidth) === n}
                        disabled={disabled}
                        onClick={() => setSelectedWidth(n)}
                        className="store-product-options-chip"
                      />
                    );
                  })}
                </div>
                <div className="store-product-options-subtitle">{labels.widthAdj}</div>
                <div className="store-product-options-grid store-product-options-grid--adjust" role="list">
                  {ADJUSTMENTS.map((a) => (
                    <OptionButton
                      key={`wadj-${a.id}`}
                      label={isRtl ? a.labelAr : a.labelEn}
                      selected={widthAdjustment === a.id}
                      onClick={() => setWidthAdjustment(a.id)}
                      className="store-product-options-chip store-product-options-chip--sub"
                    />
                  ))}
                </div>
              </div>

              <div className="store-product-options-section">
                <div className="store-product-options-head">
                  <span className="store-product-options-title">{labels.height}</span>
                  {selectedHeight != null ? (
                    <span className="store-product-options-value">
                      {selectedHeight} {cmUnitLabel}
                    </span>
                  ) : null}
                </div>
                <div className="store-product-options-grid" role="list">
                  {HEIGHT_SIZES.map((n) => {
                    const disabled = false;
                    return (
                      <OptionButton
                        key={`h-${n}`}
                        label={`${n} ${cmUnitLabel}`}
                        selected={Number(selectedHeight) === n}
                        disabled={disabled}
                        onClick={() => setSelectedHeight(n)}
                        className="store-product-options-chip"
                      />
                    );
                  })}
                </div>
                <div className="store-product-options-subtitle">{labels.heightAdj}</div>
                <div className="store-product-options-grid store-product-options-grid--adjust" role="list">
                  {ADJUSTMENTS.map((a) => (
                    <OptionButton
                      key={`hadj-${a.id}`}
                      label={isRtl ? a.labelAr : a.labelEn}
                      selected={heightAdjustment === a.id}
                      onClick={() => setHeightAdjustment(a.id)}
                      className="store-product-options-chip store-product-options-chip--sub"
                    />
                  ))}
                </div>
              </div>

              {isRollCurtain ? (
                <div className="store-product-options-section store-product-options-section--full">
                  <div className="store-product-options-head">
                    <span className="store-product-options-title">{labels.chain}</span>
                  </div>
                  <div
                    className="store-product-options-grid store-product-options-grid--chain"
                    role="list"
                  >
                    <OptionButton
                      label={labels.metal}
                      selected={chainType === "metal"}
                      onClick={() => setChainType("metal")}
                      className="store-product-options-chip store-product-options-chip--chain"
                    />
                    <OptionButton
                      label={labels.plastic}
                      selected={chainType === "plastic"}
                      onClick={() => setChainType("plastic")}
                      className="store-product-options-chip store-product-options-chip--chain"
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
          {hasSizeChart ? (
            <div className="store-product-detail-meta-row">
              <button
                type="button"
                className="store-product-detail-sizechart-trigger"
                onClick={() => setIsSizeChartOpen(true)}
              >
                <i className="fa-solid fa-ruler-combined" aria-hidden="true" />
                <span>{t("store.sizeChart.open", { defaultValue: "Size chart" })}</span>
              </button>
            </div>
          ) : null}
          <div className="store-product-detail-actions">
            <button
              type="button"
              className="store-product-detail-add-cart"
              onClick={handleAddToCart}
              disabled={outOfStock}
            >
              {outOfStock
                ? t("store.outOfStock", { defaultValue: "Out of stock" })
                : t("store.addToCart", { defaultValue: "Add to cart" })}
            </button>
            <button
              type="button"
              className="store-product-detail-share"
              onClick={handleShare}
              aria-label={t("store.shareProduct", { defaultValue: "Share product" })}
              title={t("store.shareProduct", { defaultValue: "Share product" })}
            >
              <i className="fa-solid fa-share-nodes" aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`store-product-detail-wishlist${isInWishlist(product._id) ? " is-active" : ""}`}
              onClick={() => toggleWishlist(product)}
              aria-label={
                isInWishlist(product._id)
                  ? t("store.wishlistRemove", { defaultValue: "Remove from wishlist" })
                  : t("store.wishlistAdd", { defaultValue: "Add to wishlist" })
              }
            >
              <i className={isInWishlist(product._id) ? "fa-solid fa-heart" : "fa-regular fa-heart"} />
            </button>
          </div>
        </div>
      </div>

      {customerReviews.length > 0 && (
        <section className="store-product-reviews-section">
          <div className="store-product-reviews-heading">
            <span className="store-related-heading-line" />
            <h2 className="store-related-heading-text">
              {isRtl ? "آراء العملاء" : "Customer reviews"}
            </h2>
            <span className="store-related-heading-line" />
          </div>
          <div className="store-product-reviews-avg">
            <span className="store-product-reviews-avg-num">{reviewsAvg.toFixed(1)}</span>
            <span className="store-product-reviews-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <span key={s} className={`store-product-reviews-star ${s <= Math.round(reviewsAvg) ? "is-filled" : ""}`}>★</span>
              ))}
            </span>
            <span className="store-product-reviews-count">({customerReviews.length})</span>
          </div>
          <ul className="store-product-reviews-list">
            {customerReviews.map((review) => (
              <li key={review._id} className="store-product-review-card">
                <div className="store-product-review-header">
                  <span className="store-product-review-name">{review.customerName}</span>
                  <span className="store-product-review-stars">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <span key={s} className={`store-product-reviews-star ${s <= review.rating ? "is-filled" : ""}`}>★</span>
                    ))}
                  </span>
                  <span className="store-product-review-date">
                    {new Date(review.createdAt).toLocaleDateString(isRtl ? "ar-SA" : "en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                  </span>
                </div>
                {review.comment && <p className="store-product-review-comment">{review.comment}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {relatedProducts.length > 0 && (
        <section className="store-related">
          {/* Centered heading */}
          <div className="store-related-heading">
            <span className="store-related-heading-line" />
            <h2 className="store-related-heading-text">
              {isRtl ? "منتجات قد تعجبك" : "You might also like"}
            </h2>
            <span className="store-related-heading-line" />
          </div>

          {/* Carousel — dir=ltr on the wrapper makes scrollLeft predictable regardless of page direction */}
          <div
            className="store-related-carousel"
            dir="ltr"
            onMouseEnter={() => { relPausedRef.current = true; }}
            onMouseLeave={() => { relPausedRef.current = false; }}
          >
            <button type="button" className="store-related-arrow store-related-arrow--prev" onClick={relPrevFn} aria-label="Previous">
              <i className="uil uil-angle-left-b" />
            </button>

            <div className="store-related-track" ref={relatedScrollRef}>
              {relatedProducts.map((p) => {
                const img = p.images?.[0]?.url || p.image?.url || "";
                const name = isRtl ? (p.arName || p.name) : (p.name || p.arName);
                const finalPrice = p.final_price ?? p.price ?? 0;
                const origPrice = p.original_price ?? null;
                const hasDiscount = origPrice && origPrice > finalPrice;
                const discountPct = hasDiscount ? Math.round(((origPrice - finalPrice) / origPrice) * 100) : 0;
                const relOutOfStock = isStoreProductOutOfStock(p);
                return (
                  <Link
                    key={p._id}
                    to={`/store/product/${p._id}`}
                    className="store-related-card"
                    dir={isRtl ? "rtl" : "ltr"}
                  >
                    <div className="store-related-card-img-wrap">
                      {img ? (
                        <img src={resolveProductUrl(img)} alt={name || ""} className="store-related-card-img" loading="lazy" />
                      ) : (
                        <div className="store-related-card-placeholder"><i className="uil uil-image" /></div>
                      )}
                      {hasDiscount && (
                        <span className="store-related-card-badge">-{discountPct}%</span>
                      )}
                      <div className="store-related-card-overlay">
                        <span className="store-related-card-cta">
                          {isRtl ? "عرض المنتج" : "View product"}
                          <i className={`uil ${isRtl ? "uil-arrow-left" : "uil-arrow-right"}`} />
                        </span>
                      </div>
                    </div>
                    <div className="store-related-card-body">
                      <p className="store-related-card-name">{name}</p>
                      {relOutOfStock ? (
                        <span className="store-related-card-oos">
                          {isRtl ? "نفد المخزون" : "Out of stock"}
                        </span>
                      ) : (
                        <div className="store-related-card-price">
                          {hasDiscount && (
                            <span className="store-related-card-orig">
                              {isRtl ? "ر.س" : "SAR"} {Number(origPrice).toLocaleString()}
                            </span>
                          )}
                          <span className="store-related-card-final">
                            {isRtl ? "ر.س" : "SAR"} {Number(finalPrice).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>

            <button type="button" className="store-related-arrow store-related-arrow--next" onClick={relNextFn} aria-label="Next">
              <i className="uil uil-angle-right-b" />
            </button>
          </div>
        </section>
      )}

      {isSizeChartOpen && (
        <div
          className="store-sizechart-overlay"
          role="presentation"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setIsSizeChartOpen(false);
          }}
        >
          <div
            className="store-sizechart-modal store-sizechart-modal--drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-sizechart-title"
            dir={isRtl ? "rtl" : "ltr"}
            lang={isRtl ? "ar" : "en"}
          >
            <div className="store-sizechart-header">
              <div className="store-sizechart-headings">
                <div id="store-sizechart-title" className="store-sizechart-title">
                  {sizeChartTitle}
                </div>
                {sizeChartSubtitle ? (
                  <div className="store-sizechart-subtitle">{sizeChartSubtitle}</div>
                ) : null}
              </div>
              <button
                type="button"
                className="store-sizechart-close"
                onClick={() => setIsSizeChartOpen(false)}
                aria-label={t("store.sizeChart.close", { defaultValue: "Close" })}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            </div>

            <div className="store-sizechart-controls">
              <div className="store-sizechart-unitBadge" aria-hidden="true">
                {t("store.sizeChart.unit", { defaultValue: "Unit" })}:{" "}
                {t(`store.sizeChart.units.${effectiveSizeChartUnit}`, { defaultValue: effectiveSizeChartUnit })}
              </div>
              <button
                type="button"
                className="store-sizechart-print"
                onClick={() => {
                  try {
                    document.documentElement.classList.add("store-print-sizechart");
                    document.body.classList.add("store-print-sizechart");
                    window.print();
                  } finally {
                    // cleanup also happens in afterprint, keep this as a fallback
                    setTimeout(() => {
                      document.documentElement.classList.remove("store-print-sizechart");
                      document.body.classList.remove("store-print-sizechart");
                    }, 1500);
                  }
                }}
              >
                <i className="fa-solid fa-print" aria-hidden="true" />
                <span>{t("store.sizeChart.print", { defaultValue: "Print / Download" })}</span>
              </button>
            </div>

            <div className="store-sizechart-table-wrap">
              <table className="store-sizechart-table">
                <thead>
                  <tr>
                    {sizeChartColumns.map((c, idx) => (
                      <th
                        key={`${c}-${idx}`}
                        scope="col"
                        className={idx === 0 && sizeChartColumns.length >= 3 ? "is-sticky-col" : undefined}
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayedSizeChartRows.map((row, i) => (
                    <tr key={i}>
                      {row.map((cell, j) =>
                        j === 0 ? (
                          <th
                            key={j}
                            scope="row"
                            className={sizeChartColumns.length >= 3 ? "is-sticky-col" : undefined}
                          >
                            {cell}
                          </th>
                        ) : (
                          <td key={j}>{cell}</td>
                        )
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {sizeChartNote ? <div className="store-sizechart-note">{sizeChartNote}</div> : null}
          </div>
        </div>
      )}
    </div>
    </>
  );
}
